import api from '../api/client';
import React, { useState, useEffect } from 'react';
import { Card, Select, Progress, Typography } from 'antd';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import config from '../config';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const { Title: AntTitle } = Typography;
const { Option } = Select;

const CropGrowthChart = ({ cropType }) => {
    const [growthStages, setGrowthStages] = useState([]);
    const [currentProgress, setCurrentProgress] = useState(0);
    const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
    const apiUrl = config[environment].apiUrl;

    useEffect(() => {
        fetch(`/api/crop-growth?cropType=${cropType}`)
            .then((res) => res.json())
            .then((data) => {
                setGrowthStages(data);
                // Calculate current progress based on current date (mock logic)
                const totalDays = data.reduce((sum, stage) => sum + stage.duration, 0);
                const elapsedDays = totalDays * 0.6; // Mock: 60% through season
                const currentStageIndex = data.findIndex(
                    (stage) => elapsedDays <= data.slice(0, data.indexOf(stage) + 1).reduce((sum, s) => sum + s.duration, 0)
                );
                setCurrentProgress(currentStageIndex >= 0 ? data[currentStageIndex].progress : 0);
            });
    }, [cropType]);

    const growthChartData = {
        labels: growthStages.map((stage) => stage.stage),
        datasets: [
            {
                label: 'Duration (Days)',
                data: growthStages.map((stage) => stage.duration),
                backgroundColor: 'rgba(75, 192, 192, 0.8)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1
            },
            {
                label: 'Progress (%)',
                data: growthStages.map((stage) => stage.progress),
                backgroundColor: 'rgba(153, 102, 255, 0.8)',
                borderColor: 'rgba(153, 102, 255, 1)',
                borderWidth: 1,
                type: 'line',
                fill: false,
                yAxisID: 'y1'
            }
        ]
    };

    const growthChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top'
            },
            title: {
                display: true,
                text: `${cropType.toUpperCase()} Growth Stages Timeline`
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Duration (Days)'
                }
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: 'Progress (%)'
                },
                max: 100,
                grid: {
                    drawOnChartArea: false
                }
            }
        }
    };

    const currentStage = growthStages.find((stage) => stage.progress >= currentProgress) || growthStages[0];

    return (
        <Card title="Crop Growth Analytics" bordered={false}>
            <div style={{ marginBottom: 24 }}>
                <AntTitle level={5}>Current Growth Progress</AntTitle>
                <Progress percent={currentProgress} status="active" style={{ maxWidth: 400 }} />
                <p>
                    <strong>Current Stage:</strong> {currentStage?.stage || 'Planting'}
                </p>
                <p>
                    <strong>Expected Duration:</strong> {currentStage?.duration || 0} days
                </p>
            </div>

            <Bar data={growthChartData} options={growthChartOptions} />
        </Card>
    );
};

export default CropGrowthChart;
